import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Star,
  StarOff,
  Calendar,
  AlertCircle,
  Power,
  Settings,
  Clock,
  Lock,
  Zap,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { Actuator, ActuatorCategory } from '../../../types/actuators';
import { cn } from '../../../utils/cn';
import { FeatureCard } from '../FeatureCard';
import { DimmerKnob } from '../DimmerKnob/DimmerKnob';
import {
  PeristalticPumpIcon,
  SolenoidValveIcon,
  HeaterIcon,
  GrowLightIcon,
  MixerIcon,
  CoolingValveIcon,
  PumpIcon,
  OzonizerIcon,
} from '../Icons';
import type { ActuatorCardProps } from './ActuatorCard.types';

// ============================================================
// ActuatorCard - Level 3 Domain Card
// Wraps FeatureCard (Level 2). NO card-in-card.
// See: DESIGN_SYSTEM.md for architecture rules.
// ============================================================

// --- Icon selection (unchanged) ---
const getActuatorIcon = (actuator: Actuator) => {
  const id = actuator.id.toLowerCase();
  const name = actuator.name.toLowerCase();

  if (
    id.includes('peristal') ||
    name.includes('peristal') ||
    id.includes('ph_plus') ||
    id.includes('ph_minus') ||
    id.includes('nutrient')
  ) {
    return <PeristalticPumpIcon className="w-5 h-5" scale={1.6} />;
  }
  if (id.includes('cooling') || id.includes('chlaz')) {
    return <CoolingValveIcon className="w-full h-full" />;
  }
  if (id.includes('solenoid') || name.includes('solenoid') || actuator.category === 'valve') {
    return <SolenoidValveIcon className="w-full h-full" />;
  }
  if (
    id.includes('circulation') ||
    id.includes('suction') ||
    name.includes('obehov') ||
    name.includes('sací')
  ) {
    return <PumpIcon className="w-5 h-5" scale={1.6} />;
  }
  if (actuator.category === 'mixer' || id.includes('mixer') || id.includes('mich')) {
    return <MixerIcon className="w-full h-full" />;
  }
  if (actuator.category === 'heater' || id.includes('heater') || name.includes('topení')) {
    return <HeaterIcon className="w-full h-full" />;
  }
  if (actuator.category === 'light' || id.includes('light') || name.includes('osvětlení')) {
    return <GrowLightIcon className="w-full h-full" />;
  }
  if (id.includes('ozon') || name.includes('ozon')) {
    return <OzonizerIcon className="w-full h-full" />;
  }
  return <Zap className="w-5 h-5" />;
};

// --- Category colors ---
const getCategoryColor = (category: ActuatorCategory): string => {
  switch (category) {
    case 'pump':
      return '#3b82f6';
    case 'valve':
      return '#22c55e';
    case 'heater':
      return '#ef4444';
    case 'light':
      return '#eab308';
    case 'mixer':
      return '#8b5cf6';
    default:
      return '#6b7280';
  }
};

// --- Smart color selection for special actuators ---
const getActuatorColor = (actuator: Actuator): string => {
  const id = actuator.id.toLowerCase();
  if (id.includes('peristal') || id.includes('ph_') || id.includes('nutrient')) {
    return '#14b8a6'; // Teal-500
  }
  if (id.includes('cooling') || id.includes('chlaz')) {
    return '#0ea5e9'; // Sky-500
  }
  return getCategoryColor(actuator.category);
};

const ACTUATOR_TONE_CLASSES: Record<
  string,
  {
    softBg: string;
    text: string;
    solidBg: string;
  }
> = {
  '#3b82f6': {
    softBg: 'bg-sky-500/10',
    text: 'text-sky-500',
    solidBg: 'bg-sky-500 text-white',
  },
  '#22c55e': {
    softBg: 'bg-green-500/10',
    text: 'text-green-500',
    solidBg: 'bg-green-500 text-white',
  },
  '#ef4444': {
    softBg: 'bg-red-500/10',
    text: 'text-red-500',
    solidBg: 'bg-red-500 text-white',
  },
  '#eab308': {
    softBg: 'bg-amber-500/10',
    text: 'text-amber-500',
    solidBg: 'bg-amber-500 text-white',
  },
  '#8b5cf6': {
    softBg: 'bg-emerald-500/10',
    text: 'text-emerald-500',
    solidBg: 'bg-emerald-500 text-white',
  },
  '#6b7280': {
    softBg: 'bg-gray-500/10',
    text: 'text-gray-500',
    solidBg: 'bg-gray-500 text-white',
  },
  '#14b8a6': {
    softBg: 'bg-teal-500/10',
    text: 'text-teal-500',
    solidBg: 'bg-teal-500 text-white',
  },
  '#0ea5e9': {
    softBg: 'bg-sky-500/10',
    text: 'text-sky-500',
    solidBg: 'bg-sky-500 text-white',
  },
};

const getActuatorToneClasses = (color: string) =>
  ACTUATOR_TONE_CLASSES[color.toLowerCase()] || ACTUATOR_TONE_CLASSES['#14b8a6'];

// --- Runtime formatting ---
const formatRuntime = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

export const ActuatorCard: React.FC<ActuatorCardProps> = ({
  actuator,
  onToggle,
  onSetValue,
  onRunTimed,
  onToggleFavorite,
  mode,
  onModeChange,
  isLoading = false,
  compact = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('components');
  const [isToggling, setIsToggling] = useState(false);

  const themeColor = getActuatorColor(actuator);
  const toneClasses = getActuatorToneClasses(themeColor);
  const isOn = actuator.state === 'ON';
  const hasError = actuator.state === 'ERROR';
  const isLight = actuator.category === 'light';
  const isPump = actuator.category === 'pump';
  const hasDimmer = (isLight || isPump) && !!onSetValue;

  // i18n-aware time-until formatter
  const formatTimeUntilI18n = (date?: Date): string => {
    if (!date) return '';
    const now = new Date();
    const diff = new Date(date).getTime() - now.getTime();
    if (diff < 0) return t('actuatorCard.timeNow');
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 1) return t('actuatorCard.timeInMoment');
    if (minutes < 60) return t('actuatorCard.timeInMinutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('actuatorCard.timeInHours', { hours, minutes: minutes % 60 });
    return t('actuatorCard.timeInDays', { count: Math.floor(hours / 24) });
  };

  const handleToggle = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isToggling || actuator.isLocked || isLoading) return;
    setIsToggling(true);
    try {
      if (mode === 'auto' && onModeChange) {
        await onModeChange('manual');
      }
      await onToggle(actuator.id);
    } finally {
      setIsToggling(false);
    }
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      await onToggleFavorite(actuator.id);
    }
  };

  const handleConfigClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/controls/${actuator.id}/config`);
  };

  const handleHistoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/controls/${actuator.id}/history`);
  };

  // ─── Compact mode (list item, not a card) ───
  if (compact) {
    return (
      <div
        className={cn(
          'flex flex-col gap-2 py-2 px-3 rounded-lg transition-all',
          isOn ? toneClasses.softBg : 'hover:bg-gray-100 dark:hover:bg-zinc-800',
        )}
      >
        <div className="flex items-center gap-2">
          <div
            className={cn('text-xs', isOn ? toneClasses.text : 'text-gray-400')}
          >
            {getActuatorIcon(actuator)}
          </div>
          <span
            className={cn(
              'flex-1 text-sm truncate',
              isOn ? 'font-medium text-gray-900 dark:text-zinc-100' : 'text-gray-500',
            )}
          >
            {actuator.name}
          </span>
          <button
            onClick={handleToggle}
            disabled={isToggling || actuator.isLocked || isLoading}
            aria-label={isOn ? t('actuatorCard.turnOff', { name: actuator.name }) : t('actuatorCard.turnOn', { name: actuator.name })}
            className={cn(
              'p-1 rounded-full transition-colors',
              'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500',
              isOn && toneClasses.solidBg,
              !isOn && 'text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-700',
            )}
          >
            <Power size={12} />
          </button>
        </div>
        
        {/* Dimmer slider in compact mode */}
        {hasDimmer && (
          <div className="px-1 pb-1">
            <DimmerKnob
              value={actuator.value ?? (isOn ? 100 : 0)}
              onChangeEnd={(val) => onSetValue?.(actuator.id, val)}
              onRunTimed={onRunTimed ? (val, dur) => onRunTimed(actuator.id, val, dur) : undefined}
              presets={[10, 25, 50, 75, 90, 100]}
              durationPresets={[5, 10, 30, 60, 300]}
              onLabel={isLight ? t('dimmer.on') : t('dimmer.pumpOn')}
              offLabel={isLight ? t('dimmer.off') : t('dimmer.pumpOff')}
              disabled={actuator.isLocked || isLoading}
              color={themeColor}
            />
          </div>
        )}
      </div>
    );
  }

  // ─── Full card mode ───
  // Uses FeatureCard as the ONLY shell. Children = domain content ONLY.
  const categoryLabel = `${actuator.category.toUpperCase()} • ${actuator.port}${actuator.relayIndex % 8}`;

  return (
    <FeatureCard
      label={categoryLabel}
      title={actuator.name}
      color={themeColor}
      icon={getActuatorIcon(actuator)}
      onClick={hasDimmer ? undefined : handleToggle}
      isActive={isOn}
      statusLabel={hasError ? t('actuatorCard.statusError') : isOn ? t('actuatorCard.statusRunning') : t('actuatorCard.statusOff')}
      statusColor={hasError ? '#ef4444' : isOn ? '#22c55e' : '#6b7280'}
      statusIcon={
        hasError ? (
          <AlertCircle className="w-3 h-3" />
        ) : isOn ? (
          <Wifi className="w-3 h-3" />
        ) : (
          <WifiOff className="w-3 h-3" />
        )
      }
    >
      {/* ═══ CHILDREN: Domain content only, NO card shell ═══ */}
      <div className="flex flex-col gap-2 mt-2 w-full">
        {/* Runtime badge (when ON) */}
        {isOn && actuator.runTimeSeconds && actuator.runTimeSeconds > 0 && (
          <div className={cn('flex items-center gap-1.5 text-xs font-semibold', toneClasses.text)}>
            <Clock size={12} />
            <span>{t('actuatorCard.runningPrefix', { duration: formatRuntime(actuator.runTimeSeconds) })}</span>
          </div>
        )}

        {/* PWM Dimmer Knob for light and pump actuators */}
        {hasDimmer && (
          <div
            className="flex justify-center py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <DimmerKnob
              value={actuator.value ?? (isOn ? 100 : 0)}
              onChangeEnd={(val) => onSetValue(actuator.id, val)}
              onToggle={() => handleToggle()}
              onRunTimed={onRunTimed ? (val, dur) => onRunTimed(actuator.id, val, dur) : undefined}
              presets={[10, 25, 50, 75, 90, 100]}
              durationPresets={[5, 10, 30, 60, 300]}
              onLabel={isLight ? t('dimmer.on') : t('dimmer.pumpOn')}
              offLabel={isLight ? t('dimmer.off') : t('dimmer.pumpOff')}
              disabled={isLoading || actuator.isLocked}
              color={themeColor}
              size={110}
            />
          </div>
        )}

        {/* Next scheduled execution */}
        {actuator.nextExecution && (
          <div className="flex items-center gap-1.5 text-xs text-teal-500 font-semibold">
            <Calendar size={12} />
            <span>{t('actuatorCard.nextPrefix', { time: formatTimeUntilI18n(actuator.nextExecution) })}</span>
          </div>
        )}

        {/* Error message */}
        {hasError && actuator.error && (
          <div className="flex items-start gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-xs">
            <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
            <span className="leading-tight font-medium">{actuator.error}</span>
          </div>
        )}

        {/* Locked indicator */}
        {actuator.isLocked && (
          <div className="flex items-center gap-1.5 text-xs text-amber-500 font-medium">
            <Lock size={12} />
            <span>{t('actuatorCard.locked')}</span>
          </div>
        )}

        {/* Footer: Actions */}
        <div className="mt-auto pt-2 flex items-center justify-between border-t border-gray-200/20 dark:border-white/5">
          {/* Left: Action buttons */}
          <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleConfigClick}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              title={t('actuatorCard.settings')}
            >
              <Settings size={14} />
            </button>
            <button
              onClick={handleHistoryClick}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              title={t('actuatorCard.history')}
            >
              <Clock size={14} />
            </button>
          </div>

          {/* Right: Favorite toggle */}
          {onToggleFavorite && (
            <button
              onClick={handleFavoriteClick}
              aria-label={actuator.isFavorite ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                'focus-visible:ring-2 focus-visible:ring-yellow-500',
                actuator.isFavorite
                  ? 'text-yellow-500 bg-yellow-500/10'
                  : 'text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-800',
              )}
            >
              {actuator.isFavorite ? (
                <Star size={14} className="fill-current" />
              ) : (
                <StarOff size={14} />
              )}
            </button>
          )}
        </div>
      </div>
    </FeatureCard>
  );
};

export default ActuatorCard;
